import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, CheckCircle2, Send, Loader2, Copy, Search } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { submitAidRequest } from '../lib/api';

const MATTER_TYPES = [
  'Family & Matrimonial',
  'Criminal Defence',
  'Property & Land',
  'Consumer Protection',
  'Women & Child Rights',
  'Labour & Employment',
  'Cyber & IT Law',
  'Civil Disputes',
  'Tenancy & Rent',
  'Other',
];

const FreeAid = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tracking, setTracking] = useState(null);
  const [form, setForm] = useState({
    fullName: '',
    state: '',
    matter: '',
    phone: '',
    description: '',
    consent: false,
  });

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fullName || !form.matter || !form.phone || !form.consent) {
      toast.error('Please complete the required fields and accept the consent.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await submitAidRequest(form);
      setTracking(res?.tracking_number || null);
      setSubmitted(true);
      toast.success('Your request has been received.', {
        description: res?.tracking_number ? `Your tracking number is ${res.tracking_number}` : 'A coordinator will call you within 24 working hours.',
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not submit. Please try again.';
      toast.error(typeof detail === 'string' ? detail : 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const copyTracking = async () => {
    if (!tracking) return;
    try {
      await navigator.clipboard.writeText(tracking);
      toast.success('Tracking number copied');
    } catch {
      toast.error('Could not copy');
    }
  };

  return (
    <div>
      <section className="bg-[#0F1B2D] text-[#F8F2E4] py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-xs tracking-[0.3em] text-[#E89530] mb-5">EQUITY</p>
          <h1 className="font-serif-besley text-5xl md:text-6xl mb-5">Free legal aid request</h1>
          <p className="text-[#C9BFA8] text-lg max-w-2xl">
            If you cannot afford a lawyer, you may qualify for completely free representation under the Legal Services Authorities Act, 1987. Tell us about your matter and we will route it to a verified advocate at no cost.
          </p>
        </div>
      </section>

      <section className="bg-[#F8F2E4] py-16">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-1">
            <div className="bg-[#EDE3CC] rounded-2xl p-8">
              <Shield className="w-8 h-8 text-[#A36B1F] mb-4" strokeWidth={1.6} />
              <h3 className="font-serif-besley text-2xl text-[#0F1B2D] mb-5">Who qualifies?</h3>
              <ul className="space-y-3 text-[15px] text-[#3a3a3a]">
                {[
                  'Women and children',
                  'Members of SC / ST communities',
                  'Persons with disability',
                  'Industrial workmen',
                  'Victims of human trafficking',
                  'Citizens below the income threshold notified by their State Legal Services Authority',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#A36B1F] mt-1 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="lg:col-span-2">
            {submitted ? (
              <div className="bg-white border border-[#E8DCC0] rounded-2xl p-10 text-center">
                <CheckCircle2 className="w-14 h-14 text-[#E89530] mx-auto mb-5" strokeWidth={1.6} />
                <h2 className="font-serif-besley text-3xl text-[#0F1B2D] mb-3">Request received</h2>
                <p className="text-[#3a3a3a] max-w-md mx-auto mb-6">
                  Thank you, {form.fullName.split(' ')[0]}. A coordinator will call you on <b>{form.phone}</b> within 24 working hours.
                </p>

                {tracking && (
                  <div className="bg-[#EDE3CC] border border-[#D6CAB0] rounded-xl p-5 max-w-md mx-auto mb-6">
                    <p className="text-xs tracking-[0.25em] text-[#A36B1F] mb-2">YOUR TRACKING NUMBER</p>
                    <div className="flex items-center justify-center gap-3">
                      <span className="font-serif-besley text-3xl text-[#0F1B2D] tracking-wider">{tracking}</span>
                      <button
                        type="button"
                        onClick={copyTracking}
                        className="p-2 rounded-md bg-white border border-[#D6CAB0] text-[#0F1B2D] hover:bg-[#F8F2E4] transition-colors"
                        aria-label="Copy tracking number"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-xs text-[#5a5a5a] mt-3">
                      Save this number. You can check your request status anytime at <a href="/track" className="text-[#A36B1F] underline">/track</a>.
                    </p>
                  </div>
                )}

                <div className="flex flex-wrap items-center justify-center gap-3">
                  <Link
                    to="/track"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-[#0F1B2D]/30 text-sm font-medium text-[#0F1B2D] hover:border-[#0F1B2D] transition-colors"
                  >
                    <Search className="w-4 h-4" /> Track this request
                  </Link>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setTracking(null);
                      setForm({ fullName: '', state: '', matter: '', phone: '', description: '', consent: false });
                    }}
                    className="btn-orange inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium"
                  >
                    Submit another request
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white border border-[#E8DCC0] rounded-2xl p-8 lg:p-10 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="fullName" className="text-[#0F1B2D]">Full name *</Label>
                    <Input id="fullName" value={form.fullName} onChange={(e) => update('fullName', e.target.value)} className="mt-2 h-11 border-[#E8DCC0]" />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-[#0F1B2D]">Phone number *</Label>
                    <Input id="phone" value={form.phone} onChange={(e) => update('phone', e.target.value)} className="mt-2 h-11 border-[#E8DCC0]" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-[#0F1B2D]">State</Label>
                    <Input value={form.state} onChange={(e) => update('state', e.target.value)} className="mt-2 h-11 border-[#E8DCC0]" placeholder="e.g. Karnataka" />
                  </div>
                  <div>
                    <Label className="text-[#0F1B2D]">Type of matter *</Label>
                    <Select value={form.matter} onValueChange={(v) => update('matter', v)}>
                      <SelectTrigger className="mt-2 h-11 border-[#E8DCC0]">
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                      <SelectContent>
                        {MATTER_TYPES.map((m) => (
                          <SelectItem key={m} value={m}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-[#0F1B2D]">Describe your situation</Label>
                  <Textarea
                    value={form.description}
                    onChange={(e) => update('description', e.target.value)}
                    rows={5}
                    placeholder="Share as much detail as you are comfortable with. Everything you share is confidential."
                    className="mt-2 border-[#E8DCC0]"
                  />
                </div>

                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    id="consent"
                    checked={form.consent}
                    onCheckedChange={(v) => update('consent', !!v)}
                    className="mt-1 border-[#0F1B2D]"
                  />
                  <span className="text-sm text-[#3a3a3a]">
                    I consent to LawForLayman sharing my details with a verified advocate and storing my request for the purpose of legal aid follow-up.
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-orange inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-medium disabled:opacity-70"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {submitting ? 'Submitting…' : 'Submit free aid request'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default FreeAid;
